import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawBlade } from '../../render/WeaponRenderer';
import { drawButton, pointInRect } from '../../render/ui/UI';
import type {
  ForgeModifiers,
  ForgeResult,
  HeatMap,
  Rect,
  Weapon,
  WeaponDefinition,
} from '../../types';
import { makeWeapon } from '../entities/Weapon';
import type { Phase } from '../Phase';
import { HeatSubphase } from '../subphases/HeatSubphase';
import { QuenchSubphase } from '../subphases/QuenchSubphase';
import { StrikeSubphase } from '../subphases/StrikeSubphase';
import { calculateQuality, generateDamage, qualityLabel } from '../systems/Forge';

const ADVANCE_BUTTON: Rect = { x: 80, y: 560, w: LOGICAL_WIDTH - 160, h: 56 };

interface CompleteState {
  weapon: Weapon;
  quality: number;
  burnedFrames: number;
}

export class ForgePhase implements Phase {
  private def: WeaponDefinition;
  private input: TouchInput;
  private audio: AudioManager;
  private modifiers: ForgeModifiers;

  private heatPhase: HeatSubphase;
  private strikePhase: StrikeSubphase | null = null;
  private quenchPhase: QuenchSubphase | null = null;
  private complete: CompleteState | null = null;
  private advanceTapped = false;
  private unbindCompleteTap: (() => void) | null = null;
  private burnedFramesAccum = 0;
  private heatScore = 0;
  private bestComboStreak = 0;

  constructor(
    def: WeaponDefinition,
    input: TouchInput,
    audio: AudioManager,
    modifiers: ForgeModifiers,
  ) {
    this.def = def;
    this.input = input;
    this.audio = audio;
    this.modifiers = modifiers;
    this.heatPhase = new HeatSubphase(def, input, audio);
  }

  update(dt: number): void {
    if (this.advanceTapped) return;
    if (this.complete) return;
    if (!this.strikePhase) {
      this.heatPhase.update(dt);
      if (this.heatPhase.isComplete()) {
        const r = this.heatPhase.getResult();
        this.burnedFramesAccum += r.burnedFrames;
        this.heatScore = r.heatScore;
        this.strikePhase = new StrikeSubphase(this.def, r.heatMap, this.input, this.audio);
      }
      return;
    }
    if (!this.quenchPhase) {
      this.strikePhase.update(dt);
      if (this.strikePhase.isComplete()) {
        const r = this.strikePhase.getResult();
        this.bestComboStreak = r.bestComboStreak;
        this.quenchPhase = new QuenchSubphase(this.def, r.heatMap, this.input, this.audio);
      }
      return;
    }
    this.quenchPhase.update(dt);
    if (this.quenchPhase.isComplete()) {
      this.finalize();
    }
  }

  private finalize(): void {
    if (!this.strikePhase || !this.quenchPhase) return;
    const strikeResult = this.strikePhase.getResult();
    const quenchResult = this.quenchPhase.getResult();
    const finalHeatMap: HeatMap = quenchResult.heatMap;
    const result: ForgeResult = {
      finalHeatMap,
      heatScore: this.heatScore,
      strikes: strikeResult.strikes,
      bestComboStreak: this.bestComboStreak,
      quenchExtinguished: quenchResult.extinguished,
      quenchTotal: quenchResult.total,
      quenchWarpPenalty: quenchResult.warpPenalty,
      burnedFrames: this.burnedFramesAccum,
    };
    const quality = calculateQuality(result);
    const damage = generateDamage(strikeResult.strikes);
    const weapon = makeWeapon(
      this.def,
      finalHeatMap,
      damage,
      quality,
      this.modifiers.bonusDamage,
      this.modifiers.bonusDurability,
    );
    this.complete = { weapon, quality, burnedFrames: this.burnedFramesAccum };
    this.audio.play('weapon_complete');

    this.unbindCompleteTap = this.input.onPointerDown((e) => {
      if (pointInRect(e.pos.x, e.pos.y, ADVANCE_BUTTON)) {
        this.advanceTapped = true;
      }
    });
  }

  isComplete(): boolean {
    return this.advanceTapped;
  }

  /** Returns the forged weapon. Only valid once isComplete() is true. */
  getWeapon(): Weapon {
    if (!this.complete) throw new Error('ForgePhase.getWeapon called before completion');
    return this.complete.weapon;
  }

  getQuality(): number {
    return this.complete?.quality ?? 0;
  }

  dispose(): void {
    this.unbindCompleteTap?.();
    this.unbindCompleteTap = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (this.complete) {
      this.renderComplete(ctx, this.complete);
      return;
    }
    if (this.quenchPhase) {
      this.quenchPhase.render(ctx);
    } else if (this.strikePhase) {
      this.strikePhase.render(ctx);
    } else {
      this.heatPhase.render(ctx);
    }
  }

  private renderComplete(ctx: CanvasRenderingContext2D, state: CompleteState): void {
    ctx.fillStyle = '#0a0808';
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Forge Complete', LOGICAL_WIDTH / 2, 30);

    drawBlade(ctx, state.weapon.shape, state.weapon.heat, {
      cx: LOGICAL_WIDTH / 2,
      cy: 220,
      length: 300,
      rotation: -0.15,
      damage: state.weapon.damage,
    });

    const q = state.quality;
    ctx.fillStyle = qualityColor(q);
    ctx.font = 'bold 28px system-ui, sans-serif';
    ctx.fillText(qualityLabel(q), LOGICAL_WIDTH / 2, 340);

    ctx.fillStyle = '#c9b896';
    ctx.font = '14px system-ui, sans-serif';
    ctx.textAlign = 'left';
    const panelX = 60;
    const panelY = 400;
    ctx.fillText(`Quality:    ${(q * 100).toFixed(0)}%`, panelX, panelY);
    ctx.fillText(`Damage:     ${state.weapon.damageStat}`, panelX, panelY + 22);
    ctx.fillText(`Durability: ${state.weapon.durability}`, panelX, panelY + 44);
    if (state.weapon.damage.length > 0) {
      ctx.fillStyle = '#c46a3a';
      ctx.fillText(`Imperfections: ${state.weapon.damage.length}`, panelX, panelY + 66);
    }
    if (state.burnedFrames > 30) {
      ctx.fillStyle = '#c46a3a';
      ctx.fillText('Overcooked.', panelX, panelY + 88);
    }

    drawButton(ctx, {
      rect: ADVANCE_BUTTON,
      label: 'DELIVER TO HERO',
      enabled: true,
      highlight: true,
    });
  }
}

function qualityColor(q: number): string {
  if (q >= 0.9) return '#ffd84a';
  if (q >= 0.75) return '#c9e890';
  if (q >= 0.55) return '#e6c98a';
  if (q >= 0.35) return '#c49a6a';
  return '#a06a40';
}
